'use client'

import { AddIcon } from '@repo/ui/AddIcon'
import { AttendanceNoteEntry } from '@repo/ui/AttendanceNoteEntry'
import { Avatar } from '@repo/ui/Avatar'
import { confirm as reactConfirm } from '@repo/ui/Confirm'
import { convertDateToLocalTimeInIsoFormat } from '@repo/utils/time'
import { DateSelection } from '@repo/ui/DateSelection'
import { FeeEarnerSelect } from '@repo/ui/FeeEarnerSelect'
import { formatCurrency } from '@repo/utils/numbers'
import { FormPanel } from '@repo/ui/FormPanel'
import { FormSectionTitle } from '@repo/ui/FormSectionTitle'
import { JCodeActivitySelect } from '@repo/ui/JCodeActivitySelect'
import { JCodePhaseTaskCodeSelect } from '@repo/ui/JCodePhaseTaskCodeSelect'
import { MatterSelect } from '@repo/ui/MatterSelect'
import { postTimerFormSchema, createPostingFormSchema } from '@repo/validation/schema_TimeRecording'
import { RemunerationSelect } from '@repo/ui/RemunerationSelect'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { TimeRecordingActivityRow } from '../TimeRecordingActivityRow/TimeRecordingActivityRow'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useState, useEffect, useCallback } from 'react'
import { useTimeRecordingStore } from '@repo/store/useTimeRecordingStore'
import { useToastStore } from '@repo/store/useToastStore'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const TimeRecordingForm = ({ formMode, closeForm, selectedTimerId }) => {
  const { feeEarnerId: userFeeEarnerId, feeEarnerDescription: userFeeEarnerDescription } = useAuthenticationStore()
  const { baseURL, isOutlookAddin } = useGlobalSettingsStore()
  const { matters, fetchMatter } = useMattersStore()
  const { showToast } = useToastStore()
  const {
    createTimer,
    postTimer,
    createPosting,
    fetchFeeEarnerGradeCode,
    feeEarnerGradeCode,
    setFeeEarnerGradeCode,
    fetchTimerJCodePhaseLimitWarning,
    fetchPostingJCodePhaseLimitWarning,
    fetchClientMatterCreditLimitWarnings,
    allTimers,
    activeTimer,
    deleteTimer,
    timeRecordingMatter
  } = useTimeRecordingStore()

  const selectedTimer = getSelectedTimer(allTimers, activeTimer, selectedTimerId)

  const {
    defaultClientNo,
    defaultMatterNo,
    defaultMatter,
    defaultFeeEarner,
    defaultPostingDate,
    defaultRemunerationType,
    defaultJCodePhaseTaskCode,
    defaultJCodeActivityCode,
    defaultAttendanceNote,
    defaultActivity
  } = getDefaultValues(userFeeEarnerId, userFeeEarnerDescription, formMode, selectedTimer, matters, timeRecordingMatter)

  const [clientNo, setClientNo] = useState(defaultClientNo)
  const [matterNo, setMatterNo] = useState(defaultMatterNo)
  const [matter, setMatter] = useState(defaultMatter)

  const [feeEarner, setFeeEarner] = useState(defaultFeeEarner)
  const [postingDate, setPostingDate] = useState(defaultPostingDate)

  const [remunerationType, setRemunerationType] = useState(defaultRemunerationType)

  const [activities, setActivities] = useState([defaultActivity])
  const [activityError, setActivityError] = useState(null)

  const [requiredFieldsFilled, setRequiredFieldsFilled] = useState(false)
  const [activityRowRequiredFieldsFilled, setActivityRowRequiredFieldsFilled] = useState(false)

  useEffect(() => {
    if (formMode === 'createTimer') {
      setRequiredFieldsFilled(true)
    }

    if (formMode === 'postTimer' || formMode === 'createPosting') {
      if (!matter || !feeEarner || !remunerationType || !activityRowRequiredFieldsFilled) {
        setRequiredFieldsFilled(false)
      } else {
        setRequiredFieldsFilled(true)
      }
    }
  }, [matter, feeEarner, remunerationType, activityRowRequiredFieldsFilled, formMode, setRequiredFieldsFilled])

  const [jCodePhaseTaskCode, setJCodePhaseTaskCode] = useState(defaultJCodePhaseTaskCode)
  const [jCodeActivityCode, setJCodeActivityCode] = useState(defaultJCodeActivityCode)

  const [attendanceNote, setAttendanceNote] = useState(defaultAttendanceNote)

  useEffect(() => {
    // if we click on a timer and pass in the matter, we might need to enrich the matter with the matter description etc
    enrichMatterDetails(matter, matterNo, clientNo, matters, setMatter, fetchMatter, onSelectMatter)

    // we're not including matters in the deps array below because it causes an infinite loop and isn't necessary
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientNo, matterNo, matter, fetchMatter])

  useEffect(() => {
    if (!feeEarner || !feeEarner.feeEarnerId) {
      setFeeEarnerGradeCode({ feeEarnerGradeCode: null })
      setJCodePhaseTaskCode(null)
      setJCodeActivityCode(null)
      return
    }
    fetchFeeEarnerGradeCode({ feeEarnerId: feeEarner.feeEarnerId })
  }, [feeEarner, fetchFeeEarnerGradeCode, setFeeEarnerGradeCode])

  const onSelectMatter = (matter) => {
    const { clientNo, matterNo, clientMatterNo } = matter || {}
    setClientNo(clientNo)
    setMatterNo(matterNo)
    setMatter(matter)

    if (!userFeeEarnerId) {
      setFeeEarner(
        matter?.feeEarnerId
          ? { feeEarnerId: matter.feeEarnerId, description: matter.feeEarnerDescription }
          : null
      )
    }

    let newRemunerationType = null
    const detailedMatter = matters[clientMatterNo] || null
    if (detailedMatter?.remunerationTypeId) {
      const { remunerationTypeId, remunerationTypeDescription } = detailedMatter
      newRemunerationType = {
        remunerationTypeId,
        value: remunerationTypeId,
        remunerationTypeDescription,
        description: remunerationTypeDescription
      }
    }

    setRemunerationType(newRemunerationType)
    setActivities([{ ...defaultActivity }])
  }

  const onSetRemunerationType = (remunerationType) => {
    setRemunerationType(remunerationType)
    setActivities([{ ...defaultActivity }])
  }

  const onSetActivity = useCallback(
    (activity, index) => {
      activities[index] = activity
      setActivities(activities)
    },
    [activities]
  )

  const addActivity = () => {
    setActivities([...activities, { ...defaultActivity }])
  }

  const removeActivity = (index) => {
    const filteredActivities = activities.filter((_, i) => i !== index)
    setActivities([...filteredActivities])
  }

  const getPostTimerFormParams = (getWipData) => {
    const firstActivity = activities?.length ? activities[0] : null

    if (getWipData) {
      const timeLedgerPostingsActivities = () => {
        const { timeRecordingActivity, description, rateType, quantity } = firstActivity
        const unitType = rateType === 'H' ? 'minutes' : 'units'
        const quantityOrNull = quantity || null

        return {
          timeRecordingActivityId: timeRecordingActivity?.timeRecordingActivityId || null,
          description,
          rateType,
          rateAmount: unitType === 'units' ? quantityOrNull : null,
          timeAmount: unitType === 'minutes' ? quantityOrNull : null
        }
      }

      return {
        clientNo,
        matterNo: Number(matterNo),
        remunerationTypeId: remunerationType?.remunerationTypeId || null,
        postingDate: convertDateToLocalTimeInIsoFormat(postingDate),
        feeEarnerId: feeEarner?.feeEarnerId || null,
        attendanceNote,
        jCodePhaseTaskCode: jCodePhaseTaskCode?.jCodePhaseTaskCode || null,
        jCodeActivityCode: jCodeActivityCode?.jCodeActivityCode || null,
        timeLedgerPostingsActivities: [timeLedgerPostingsActivities()]
      }
    } else {
      const { timeRecordingActivity, description, rateType, quantity } = firstActivity || {}
      const unitType = rateType === 'H' ? 'minutes' : 'units'
      const quantityOrNull = quantity || null

      return {
        matterTimerId: selectedTimer?.matterTimerId,
        clientNo,
        matterNo: Number(matterNo),
        postingDate: convertDateToLocalTimeInIsoFormat(postingDate),
        remunerationTypeId: remunerationType?.remunerationTypeId || null,
        timeRecordingActivityId: timeRecordingActivity?.timeRecordingActivityId || null,
        description,
        attendanceNote,
        feeEarnerId: feeEarner?.feeEarnerId || null,
        jCodePhaseTaskCode: jCodePhaseTaskCode?.jCodePhaseTaskCode || null,
        jCodeActivityCode: jCodeActivityCode?.jCodeActivityCode || null,
        rateType,
        rateAmount: unitType === 'units' ? quantityOrNull : null,
        timeAmount: unitType === 'minutes' ? quantityOrNull : null
      }
    }
  }

  const validatePostTimerForm = async (params) => {
    params = params || getPostTimerFormParams()

    try {
      await postTimerFormSchema.validate(params)

      if (!params.rateAmount && !params.timeAmount) {
        throw 'Rate amount or time amount must be provided'
      }

      return true
    } catch (err) {
      showToast({ type: 'error', title: 'Error', text: err.message })
      return false
    }
  }

  const checkClientMatterWipLimitWarning = async (params) => {
    const warning = await fetchClientMatterCreditLimitWarnings(params)

    if (!warning?.clientCreditLimitWarnings && !warning?.matterCreditLimitWarnings) return

    return warning
  }

  const checkTimerJCodePhaseLimitWarning = async (params) => {
    if (!params.jCodePhaseTaskCode) return null
    const warning = await fetchTimerJCodePhaseLimitWarning(params)
    return warning
  }

  const getCreatePostingFormParams = () => {
    const timeLedgerPostingsActivities = activities.map((activity) => {
      const { timeRecordingActivity, description, rateType, quantity } = activity
      const unitType = rateType === 'H' ? 'minutes' : 'units'
      const quantityOrNull = quantity || null

      return {
        timeRecordingActivityId: timeRecordingActivity?.timeRecordingActivityId || null,
        description,
        rateType,
        rateAmount: unitType === 'units' ? quantityOrNull : null,
        timeAmount: unitType === 'minutes' ? quantityOrNull : null
      }
    })

    return {
      clientNo,
      matterNo: Number(matterNo),
      remunerationTypeId: remunerationType?.remunerationTypeId || null,
      postingDate: convertDateToLocalTimeInIsoFormat(postingDate),
      feeEarnerId: feeEarner?.feeEarnerId || null,
      attendanceNote,
      jCodePhaseTaskCode: jCodePhaseTaskCode?.jCodePhaseTaskCode || null,
      jCodeActivityCode: jCodeActivityCode?.jCodeActivityCode || null,
      timeLedgerPostingsActivities
    }
  }

  const validateCreatePostingForm = async (params) => {
    params = params || getCreatePostingFormParams()

    try {
      await createPostingFormSchema.validate(params)
      return true
    } catch (err) {
      showToast({ type: 'error', title: 'Error', text: err.message })
      return false
    }
  }

  const checkPostingJCodePhaseLimitWarning = async (params) => {
    if (!params.jCodePhaseTaskCode) return null
    const warning = await fetchPostingJCodePhaseLimitWarning(params)
    return warning
  }

  const submitForm = async (e) => {
    const buttonValue = e.nativeEvent?.submitter?.value || 'Post'
    const printAfterPosting = buttonValue === 'Print'

    if (e?.preventDefault) e.preventDefault()

    const firstActivity = activities?.length ? activities[0] : null
    const { timeRecordingActivity, description } = firstActivity || {}
    let timeLedgerPostingId

    if (formMode === 'createTimer') {
      createTimer({
        clientNo,
        matterNo,
        remunerationTypeId: remunerationType?.remunerationTypeId || null,
        timeRecordingActivityId: timeRecordingActivity?.timeRecordingActivityId || null,
        description,
        attendanceNote
      })
    } else if (formMode === 'postTimer') {
      const params = getPostTimerFormParams()
      const wipParams = getPostTimerFormParams(true)
      const wipWarning = await checkClientMatterWipLimitWarning(wipParams)
      const shouldProceedWip = await showClientMatterWipLimitWarningDialog(wipWarning)
      if (!shouldProceedWip) return

      const valid = await validatePostTimerForm(params)
      if (!valid) return

      const warning = await checkTimerJCodePhaseLimitWarning(params)
      const shouldProceed = showJCodeWarningDialog(warning)
      if (!shouldProceed) return

      const res = await postTimer(params)
      timeLedgerPostingId = res?.timeLedgerPostingId || null
    } else if (formMode === 'createPosting') {
      const params = getCreatePostingFormParams()
      const wipWarning = await checkClientMatterWipLimitWarning(params)
      const shouldProceedWip = await showClientMatterWipLimitWarningDialog(wipWarning)
      if (!shouldProceedWip) return

      const valid = await validateCreatePostingForm(params)
      if (!valid) return

      const warning = await checkPostingJCodePhaseLimitWarning(params)
      const shouldProceed = await showJCodeWarningDialog(warning)
      if (!shouldProceed) return

      await createPosting(params)
    }

    if (printAfterPosting && timeLedgerPostingId) openPostingReportForPrint(timeLedgerPostingId)

    closeForm()
  }

  const showClientMatterWipLimitWarningDialog = (warning) => {
    if (!warning) return true

    let warningMessage = []

    if (warning.matterCreditLimitWarnings?.length) {
      warning.matterCreditLimitWarnings.map((matterWarning, index) => {
        const warningType = matterWarning.warningType === 'WIP' ? 'WIP' : matterWarning.warningType.toLowerCase()

        warningMessage.push(
          <MUITypography
            key={`matter-wip-1-${index}`}
            sx={{
              fontWeight: '600',
              mt: warningMessage.length ? '1.25rem' : 0,
              color: 'error.main',
              borderTop: warningMessage.length ? '1px solid #ddd' : 'none',
              paddingTop: warningMessage.length ? '1.25rem' : 0
            }}
          >
            Matter {warningType} warning threshold exceeded ({matterWarning.percentWarning}% of limit)
          </MUITypography>,
          <MUITypography key={`matter-wip-2-${index}`}>
            <strong>Limit:</strong> {formatCurrency(matterWarning.limit)}
          </MUITypography>,
          <MUITypography key={`matter-wip-3-${index}`}>
            <strong>Total if you proceed:</strong>{' '}
            {formatCurrency(matterWarning.existingValue + matterWarning.newValue)}
          </MUITypography>
        )
      })
    }

    if (warning.clientCreditLimitWarnings?.length) {
      warning.clientCreditLimitWarnings.map((clientWarning, index) => {
        const warningType = clientWarning.warningType === 'WIP' ? 'WIP' : clientWarning.warningType.toLowerCase()

        warningMessage.push(
          <MUITypography
            key={`client-wip-1-${index}`}
            sx={{
              fontWeight: '600',
              mt: warningMessage.length ? '1.25rem' : 0,
              color: 'error.main',
              borderTop: warningMessage.length ? '1px solid #ddd' : 'none',
              paddingTop: warningMessage.length ? '1.25rem' : 0
            }}
          >
            Client {warningType} warning threshold exceeded ({clientWarning.percentWarning}% of limit)
          </MUITypography>,
          <MUITypography key={`client-wip-2-${index}`}>
            <strong>Limit:</strong> {formatCurrency(clientWarning.limit)}
          </MUITypography>,
          <MUITypography key={`client-wip-3-${index}`}>
            <strong>Total if you proceed:</strong>{' '}
            {formatCurrency(clientWarning.existingValue + clientWarning.newValue)}
          </MUITypography>
        )
      })
    }

    if (warningMessage.length) {
      warningMessage.push(
        <MUITypography
          key="client-wip-4"
          sx={{ fontWeight: '600', mt: '1.5rem', borderTop: '1px solid #ddd', paddingTop: '1.25rem' }}
        >
          Are you sure you want to continue?
        </MUITypography>
      )
    }

    return warningMessage.length ? reactConfirm(warningMessage) : true
  }

  const showJCodeWarningDialog = (warning) => {
    if (!warning) return true
    return reactConfirm('You have exceeded the percentage limit for this phase. Are you sure you want to continue?')
  }

  const showPostingFields = formMode?.toLowerCase().includes('post')

  const openPostingReportForPrint = (postingId) => {
    if (!clientNo || !matterNo || !postingId) return null
    // this will open an Osprey 1 report in a new window/tab
    const url = `${baseURL}/reportlib/ReportViewer.aspx?repID=ATTNOTEL&parms=%27${clientNo}%27,%27${matterNo}%27,%27${postingId}%27`
    window.open(url, '_blank', `popup=true`)
  }

  const handleTimerDelete = async () => {
    if (!selectedTimer) return
    const proceed = await reactConfirm('Are you sure you want to delete this timer?')
    if (!proceed) return

    const { matterTimerId } = selectedTimer
    await deleteTimer({ matterTimerId })
    closeForm('timers')
  }

  return (
    <form onSubmit={submitForm} style={{ height: 'calc(100% - 110px)', margin: 0 }}>
      <MUIStack
        direction="column"
        data-testid="TimeRecordingForm"
        sx={{ padding: 0, height: '100%', mt: -4, overflow: 'hidden' }}
      >
        <MUIStack direction="column" gap={5} sx={{ flex: 1, overflowY: 'auto', padding: '0px 24px 20px' }}>
          <FormPanel>
            <FormSectionTitle title="Details" />

            <MatterSelect
              value={matter}
              onChange={onSelectMatter}
              sx={{ width: '100%' }}
              disableGlobal
              disableArchived
              includeBlankMatterOption={formMode === 'createTimer'}
              required={formMode === 'postTimer' || formMode === 'createPosting'}
              disabled={formMode === 'postTimer' && Boolean(selectedTimer?.clientNo)}
              menuZindex={9999}
              menuWidth={isOutlookAddin ? '260px' : null}
            />

            {showPostingFields ? (
              <MUIStack
                direction="row"
                gap={'20px'}
                sx={{ width: '100%', flexWrap: isOutlookAddin ? 'wrap' : 'nowrap' }}
              >
                <FeeEarnerSelect
                  value={feeEarner}
                  onChange={setFeeEarner}
                  sx={{ flex: 1, minWidth: '220px', width: '100%' }}
                  required
                  clearable={false}
                  menuZindex={9999}
                  menuWidth={isOutlookAddin ? '260px' : null}
                />
                <DateSelection
                  format="d MMM yyyy"
                  value={postingDate}
                  onChange={setPostingDate}
                  sx={{ width: isOutlookAddin ? '100%' : '230px' }}
                  label="Date"
                  enableTypedDdMmYyyy={true}
                  showAdornment={true}
                />
              </MUIStack>
            ) : null}

            <RemunerationSelect
              crmSchemeId={matter?.crmSchemeId || 0}
              privateOrLegalAid={matter?.privateOrLegalAid || ''}
              value={remunerationType}
              onChange={onSetRemunerationType}
              sx={{ width: '100%' }}
              required={formMode === 'postTimer' || formMode === 'createPosting'}
              menuZindex={9999}
              menuWidth={isOutlookAddin ? '260px' : null}
            />
          </FormPanel>

          {activities.map((activity, index) => {
            return (
              <TimeRecordingActivityRow
                index={index}
                rowCount={activities.length}
                matter={matter}
                remunerationTypeId={remunerationType?.remunerationTypeId || null}
                feeEarnerId={feeEarner?.feeEarnerId || null}
                selectedTimer={selectedTimer}
                clientNo={clientNo}
                matterNo={matterNo}
                formMode={formMode}
                key={`activity-${index}`}
                onChange={onSetActivity}
                removeActivity={removeActivity}
                onActivityError={setActivityError}
                onRequiredState={setActivityRowRequiredFieldsFilled}
              />
            )
          })}

          {formMode === 'createPosting' && !activityError ? (
            <TextLinkButton
              onClick={addActivity}
              title={
                <MUIStack direction="row" alignItems="center" gap={2}>
                  <AddIcon sx={{ width: '22px', height: '22px', color: '#385BAB' }} />
                  Add new activity
                </MUIStack>
              }
            />
          ) : null}

          <FormPanel>
            {showPostingFields && feeEarnerGradeCode ? (
              <>
                <FormSectionTitle
                  title="J Codes"
                  endElement={
                    <Avatar
                      sx={{ width: '32px', height: '32px', fontSize: '0.75rem' }}
                      name={feeEarnerGradeCode}
                      avatarSx={{ backgroundColor: 'greyShades.grey1', color: 'text.primary' }}
                      tooltipText="Fee earner grade code"
                    />
                  }
                />
                <MUIStack
                  direction="row"
                  gap={isOutlookAddin ? '20px' : '10px'}
                  sx={{ width: '100%', flexWrap: 'wrap' }}
                >
                  <JCodePhaseTaskCodeSelect
                    value={jCodePhaseTaskCode}
                    onChange={setJCodePhaseTaskCode}
                    sx={{ width: isOutlookAddin ? '100%' : 'calc(50% - 5px)' }}
                    menuZindex={9999}
                    menuWidth={isOutlookAddin ? '260px' : null}
                  />
                  <JCodeActivitySelect
                    value={jCodeActivityCode}
                    onChange={setJCodeActivityCode}
                    sx={{ width: isOutlookAddin ? '100%' : 'calc(50% - 5px)' }}
                    menuZindex={9999}
                    menuWidth={isOutlookAddin ? '260px' : null}
                    menuOffset={isOutlookAddin ? null : '-181px'}
                  />
                </MUIStack>
              </>
            ) : null}
          </FormPanel>

          <FormPanel>
            <FormSectionTitle title="Attendance note" />
            <AttendanceNoteEntry
              value={attendanceNote}
              onChange={(value) => setAttendanceNote(value)}
              workflowTypeId={matter?.workflowTypeId || ''}
            />
          </FormPanel>
        </MUIStack>

        <MUIStack
          direction={isOutlookAddin ? 'column-reverse' : 'row'}
          sx={{
            padding: '20px 24px',
            paddingBottom: isOutlookAddin ? 0 : '20px',
            width: '100%',
            borderTop: '1px solid rgba(5, 28, 51, 0.12)',
            backgroundColor: 'white'
          }}
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <MUIStack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={4}
            sx={{ width: isOutlookAddin ? '100%' : 'auto' }}
          >
            <MUITypography
              variant="body1"
              sx={{
                mb: 0,
                color: 'text.secondary',
                userSelect: 'none',
                cursor: 'pointer',
                width: isOutlookAddin ? '100%' : 'auto',
                textAlign: isOutlookAddin ? 'center' : 'left'
              }}
              onClick={closeForm}
            >
              Cancel
            </MUITypography>
          </MUIStack>

          <MUIStack
            direction={isOutlookAddin ? 'column-reverse' : 'row'}
            sx={{ width: isOutlookAddin ? '100%' : 'auto' }}
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
          >
            {formMode === 'postTimer' && selectedTimer && !isOutlookAddin ? (
              <MUIButton
                variant="outlined"
                color="error"
                onClick={handleTimerDelete}
                sx={{ width: isOutlookAddin ? '100%' : 'auto' }}
              >
                Delete
              </MUIButton>
            ) : null}

            {formMode !== 'createTimer' && activities.length === 1 ? (
              <MUIButton
                variant="outlined"
                color="primary"
                type="submit"
                value="Print"
                disabled={!!activityError || !requiredFieldsFilled}
                sx={{ width: isOutlookAddin ? '100%' : 'auto' }}
              >
                Post & print
              </MUIButton>
            ) : null}

            <MUIButton
              variant="contained"
              color="primary"
              type="submit"
              value="Post"
              disabled={!!activityError || !requiredFieldsFilled}
              sx={{ width: isOutlookAddin ? '100%' : 'auto' }}
            >
              {getFormButtonLabel(formMode)}
            </MUIButton>
          </MUIStack>
        </MUIStack>
      </MUIStack>
    </form>
  )
}

const getFormButtonLabel = (formMode) => {
  switch (formMode) {
    case 'createPosting':
      return 'Post time'
    case 'createTimer':
      return 'Start timer'
    case 'postTimer':
      return 'Post timer'
    default:
      return 'Save'
  }
}

const getDefaultValues = (
  userFeeEarnerId,
  userFeeEarnerDescription,
  formMode,
  selectedTimer,
  matters,
  timeRecordingMatter
) => {
  const currentMatter = timeRecordingMatter // from useTimeRecordingStore
  const currentMatterIsValid = currentMatter && !currentMatter.dateArchived && currentMatter.workflowTypeId !== 'GLOBAL'

  let defaultClientNo = currentMatterIsValid ? currentMatter.clientNo : null
  let defaultMatterNo = currentMatterIsValid ? currentMatter.matterNo : null
  let defaultMatter = currentMatterIsValid ? currentMatter : null

  let defaultPostingDate = new Date()

  let defaultRemunerationType =
    currentMatterIsValid && currentMatter.remunerationTypeId
      ? {
          description: currentMatter.remunerationTypeDescription,
          remunerationTypeDescription: currentMatter.remunerationTypeDescription,
          remunerationTypeId: currentMatter.remunerationTypeId,
          value: currentMatter.remunerationTypeId
        }
      : null

  let defaultTimeRecordingActivity = null
  let defaultDescription = ''

  let defaultJCodePhaseTaskCode = null
  let defaultJCodeActivityCode = null

  let defaultAttendanceNote = ''

  let defaultActivity = {
    timeRecordingActivity: null,
    description: '',
    rateType: null,
    rateAmount: null,
    timeAmount: null,
    quantity: null
  }

  if (formMode === 'postTimer' && selectedTimer) {
    const {
      clientNo,
      matterNo,
      remunerationTypeId,
      remunerationDescription,
      timeRecordingActivityId,
      activityDescription,
      description,
      attendanceNote
    } = selectedTimer

    defaultClientNo = clientNo
    defaultMatterNo = matterNo

    let clientMatterNo = `${clientNo || ''}-${matterNo}`
    defaultMatter = matters[clientMatterNo] ? matters[clientMatterNo] : { clientNo, matterNo, clientMatterNo }

    if (!clientNo && matterNo === 0) {
      // "Blank matter"
      defaultClientNo = null
      defaultMatterNo = null
      clientMatterNo = null
      defaultMatter = null
    }

    defaultAttendanceNote = attendanceNote
    defaultRemunerationType = remunerationTypeId ? { remunerationTypeId, description: remunerationDescription } : null

    defaultDescription = description
    defaultTimeRecordingActivity = timeRecordingActivityId
      ? {
          timeRecordingActivityId,
          timeRecordingActivityDescription: activityDescription
        }
      : null

    defaultActivity = {
      ...defaultActivity,
      description: defaultDescription || '',
      timeRecordingActivity: defaultTimeRecordingActivity
    }
  }

  let defaultFeeEarner = null
  if (userFeeEarnerId) {
    defaultFeeEarner = { feeEarnerId: userFeeEarnerId, description: userFeeEarnerDescription }
  } else if (defaultMatter?.feeEarnerId) {
    defaultFeeEarner = {
      feeEarnerId: defaultMatter.feeEarnerId,
      description: defaultMatter.feeEarnerDescription
    }
  }

  return {
    defaultClientNo,
    defaultMatterNo,
    defaultMatter,
    defaultFeeEarner,
    defaultPostingDate,
    defaultRemunerationType,
    defaultTimeRecordingActivity,
    defaultDescription,
    defaultJCodePhaseTaskCode,
    defaultJCodeActivityCode,
    defaultAttendanceNote,
    defaultActivity
  }
}

const enrichMatterDetails = async (matter, matterNo, clientNo, matters, setMatter, fetchMatter, onSelectMatter) => {
  // if we click on a timer and pass in the matter, we might need to enrich the matter with the matter description etc

  // don't do this is the matter is already encriched
  if (matter && matter.matterDescription && matter.remunerationTypeId) return

  // this is null, or is a blank matter, don't need to do anything
  if (!clientNo && !matterNo) return

  const clientMatterNo = `${clientNo}-${matterNo}`

  // if we can get it from the store, do that
  if (matters[clientMatterNo]) {
    setMatter(matters[clientMatterNo])
  }

  if (matters[clientMatterNo]?.remunerationTypeId) {
    // we have the full extended matter
    return
  }

  // fetch the extended matter from the server
  const fetchedMatter = await fetchMatter({ clientMatterNo })
  onSelectMatter(fetchedMatter)
}

const getSelectedTimer = (allTimers, activeTimer, selectedTimerId) => {
  if (activeTimer?.matterTimerId === selectedTimerId) return activeTimer

  const timer = allTimers.find((t) => t.matterTimerId === selectedTimerId) || null
  return timer
}

TimeRecordingForm.propTypes = {
  formMode: PropTypes.string,
  closeForm: PropTypes.func,
  selectedTimerId: PropTypes.any
}
